/* global google */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Grid, TextField, Typography, Box, IconButton, Autocomplete } from "@mui/material";
import { Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Close, Place as PlaceIcon, InfoOutlined, DeleteOutline, AddAPhoto, AddPhotoAlternate } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import PlaceApi from "Services/Place";
import { buildTypeOptions } from "./placeTypes";

const TYPE_OPTIONS = buildTypeOptions();

const getAddressComponent = (components = [], type) => {
  const found = components.find((c) => c.types?.includes(type));
  return found?.long_name || "";
};

const extractPlaceMeta = ({ place, geocodeResult, lat, lng }) => {
  const source = place || geocodeResult || {};
  const components = source?.address_components || [];
  const viewport = source?.geometry?.viewport;

  const south = viewport?.getSouthWest ? viewport.getSouthWest().lat() : lat - 0.00005;
  const west = viewport?.getSouthWest ? viewport.getSouthWest().lng() : lng - 0.00005;
  const north = viewport?.getNorthEast ? viewport.getNorthEast().lat() : lat + 0.00005;
  const east = viewport?.getNorthEast ? viewport.getNorthEast().lng() : lng + 0.00005;

  return {
    source: "google",
    layer: source?.types?.[0] || "venue",
    sourceId: source?.place_id ? `google:place:${source.place_id}` : "",
    addressName: source?.name || source?.formatted_address || "",
    addressNumber: getAddressComponent(components, "street_number"),
    addressStreet: getAddressComponent(components, "route"),
    addressLocality: getAddressComponent(components, "locality") || getAddressComponent(components, "postal_town"),
    addressCountry: getAddressComponent(components, "country"),
    addressRegion: getAddressComponent(components, "administrative_area_level_1"),
    addressCounty: getAddressComponent(components, "administrative_area_level_2"),
    addressNeighbourhood: getAddressComponent(components, "neighborhood") || getAddressComponent(components, "sublocality"),
    addressPlusCode: source?.plus_code?.global_code || source?.plus_code?.compound_code || "",
    propertiesSubcategory: source?.types?.[1] || source?.types?.[0] || "",
    propertiesImage: "",
    listImages: [],
    centroidLat: lat,
    centroidLon: lng,
    boundingBoxMinLat: south,
    boundingBoxMaxLat: north,
    boundingBoxMinLon: west,
    boundingBoxMaxLon: east,
    osmId: "",
    osmType: "",
    admin_level: Number(getAddressComponent(components, "administrative_area_level_2") ? 6 : 0),
  };
};

const MapPicker = ({ marker, setMarker, onPlaceMeta }) => {
  const map = useMap();
  const places = useMapsLibrary("places");
  const [mapMarker, setMapMarker] = useState(null);
  const geocoderRef = useRef(null);

  useEffect(() => {
    if (!map || geocoderRef.current) return;
    geocoderRef.current = new google.maps.Geocoder();
  }, [map]);

  const reverseGeocode = (lat, lng) => {
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        onPlaceMeta?.(extractPlaceMeta({ geocodeResult: results[0], lat, lng }));
      }
    });
  };

  const bindDragListener = (googleMarker) => {
    googleMarker.addListener("dragend", () => {
      const pos = googleMarker.getPosition();
      if (!pos) return;
      const nextMarker = { lat: pos.lat(), lng: pos.lng() };
      setMarker(nextMarker);
      reverseGeocode(nextMarker.lat, nextMarker.lng);
    });
  };

  useEffect(() => {
    if (!map) return;
    const clickListener = map.addListener("click", (e) => {
      if (!e?.latLng) return;
      const nextMarker = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      setMarker(nextMarker);
      reverseGeocode(nextMarker.lat, nextMarker.lng);
    });

    return () => {
      if (clickListener) clickListener.remove();
    };
  }, [map]);

  useEffect(() => {
    if (!map || !places) return;
    const input = document.getElementById("add-place-search");
    if (!input) return;

    const autocomplete = new places.Autocomplete(input);
    autocomplete.bindTo("bounds", map);

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place?.geometry?.location) return;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setMarker({ lat, lng });
      onPlaceMeta?.(extractPlaceMeta({ place, lat, lng }));
      map.panTo({ lat, lng });
      map.setZoom(15);
    });
  }, [map, places, onPlaceMeta, setMarker]);

  useEffect(() => {
    if (!map) return;
    if (!marker) {
      if (mapMarker) {
        mapMarker.setMap(null);
        setMapMarker(null);
      }
      return;
    }

    map.panTo(marker);
    if ((map.getZoom?.() || 0) < 13) map.setZoom(15);

    if (!mapMarker) {
      const newMarker = new google.maps.Marker({
        position: marker,
        map,
        draggable: true,
      });
      bindDragListener(newMarker);
      newMarker.__dragBound = true;
      setMapMarker(newMarker);
      return;
    }

    mapMarker.setPosition(marker);
    mapMarker.setDraggable(true);
    if (!mapMarker.__dragBound) {
      bindDragListener(mapMarker);
      mapMarker.__dragBound = true;
    }
    mapMarker.setMap(map);
  }, [map, marker, mapMarker]);

  useEffect(() => {
    return () => {
      if (mapMarker) mapMarker.setMap(null);
    };
  }, [mapMarker]);

  return (
    <div style={{ position: "relative" }}>
      <TextField
        id="add-place-search"
        placeholder="Search location"
        size="small"
        style={{ position: "absolute", right: 10, top: 10, zIndex: 2, background: "#fff", width: 260 }}
      />
      <Map defaultZoom={6} defaultCenter={{ lat: 11.1271, lng: 78.6569 }} style={{ width: "100%", height: 450, borderRadius: 10 }} />
    </div>
  );
};

const AddPlaceForm = ({ onSuccess, onCancel, placeId = "" }) => {
  const dispatch = useDispatch();
  const [marker, setMarker] = useState(null);
  const [placeMeta, setPlaceMeta] = useState({});
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const isEditMode = Boolean(placeId);

  const formik = useFormik({
    initialValues: {
      name: "",
      type: "venue",
    },
    validationSchema: yup.object({
      name: yup.string().trim().required("Location Name is required"),
      type: yup.string().trim().required("Type is required"),
    }),
    onSubmit: (values) => {
      if (!marker) {
        dispatch(
          showSnackbar({
            message: "Selectionnez un point sur la map",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "warning",
          })
        );
        return;
      }

      dispatch(showLoader("Loading please wait..."));
      const today = new Date().toISOString().slice(0, 10);
      const payload = {
        name: values.name,
        type: values.type,
        source: placeMeta?.source || "google",
        layer: placeMeta?.layer || values.type,
        sourceId: placeMeta?.sourceId || "",
        addressName: placeMeta?.addressName || values.name,
        addressNumber: placeMeta?.addressNumber || "",
        addressStreet: placeMeta?.addressStreet || "",
        addressLocality: placeMeta?.addressLocality || "",
        addressCountry: placeMeta?.addressCountry || "",
        addressRegion: placeMeta?.addressRegion || "",
        addressCounty: placeMeta?.addressCounty || "",
        addressNeighbourhood: placeMeta?.addressNeighbourhood || "",
        addressPlusCode: placeMeta?.addressPlusCode || "",
        propertiesCategory: values.type,
        propertiesSubcategory: placeMeta?.propertiesSubcategory || values.type,
        propertiesWikidata: "",
        propertiesWikipedia: "",
        propertiesImage: placeMeta?.propertiesImage || "",
        propertiesDescription: "",
        listImages: placeMeta?.listImages || [],
        status: "ACTIVE",
        dateAdded: today,
        centroidLat: marker.lat,
        centroidLon: marker.lng,
        boundingBoxMinLat: placeMeta?.boundingBoxMinLat ?? marker.lat - 0.00005,
        boundingBoxMaxLat: placeMeta?.boundingBoxMaxLat ?? marker.lat + 0.00005,
        boundingBoxMinLon: placeMeta?.boundingBoxMinLon ?? marker.lng - 0.00005,
        boundingBoxMaxLon: placeMeta?.boundingBoxMaxLon ?? marker.lng + 0.00005,
        propertiesAttraction: 1,
        osmId: placeMeta?.osmId || "",
        admin_level: placeMeta?.admin_level || 0,
      };

      const maybeClientLoginId = (placeMeta?.clientLoginId || "").trim();
      const maybePartnerLoginId = (placeMeta?.partnerLoginId || "").trim();
      const maybeOsmType = (placeMeta?.osmType || "").trim().toUpperCase();
      const validOsmType = ["N", "W", "R"];

      if (maybeClientLoginId) payload.clientLoginId = maybeClientLoginId;
      if (maybePartnerLoginId) payload.partnerLoginId = maybePartnerLoginId;
      if (validOsmType.includes(maybeOsmType)) payload.osmType = maybeOsmType;

      console.log("[AddPlace] request body (formatted) ->");
      console.log(JSON.stringify(payload, null, 2));
      

      const request = isEditMode ? PlaceApi.updatePlaceById(placeId, payload) : PlaceApi.addPlace(payload);
      request.then(
        (res) => {
          dispatch(hideLoader());
          if (res?.status === 200 || res?.status === 201) {
            dispatch(
              showSnackbar({
                message: res?.data?.message || (isEditMode ? "Place modifie avec succes" : "Place ajoute avec succes"),
                autoHideDuration: 3000,
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "success",
              })
            );
            onSuccess?.();
          }
        },
        (err) => {
          dispatch(hideLoader());
          dispatch(
            showSnackbar({
              message: err?.response?.data?.message || (isEditMode ? "Erreur lors de la modification du place" : "Erreur lors de l'ajout du place"),
              autoHideDuration: 3000,
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "error",
            })
          );
        }
      );
    },
  });

  useEffect(() => {
    if (!isEditMode) {
      formik.setValues({ name: "", type: "venue" });
      setMarker(null);
      setPlaceMeta({});
      setInitialSnapshot(null);
      return;
    }

    dispatch(showLoader("Loading place details..."));
    PlaceApi.getPlaceById(placeId).then(
      (res) => {
        dispatch(hideLoader());
        const place = res?.data?.data?.data?.[0] || res?.data?.data?.[0] || res?.data?.data || null;
        if (!place) return;

        formik.setValues({
          name: place?.name || "",
          type: place?.type || "venue",
        });

        if (place?.centroidLat !== null && place?.centroidLon !== null) {
          setMarker({ lat: Number(place.centroidLat), lng: Number(place.centroidLon) });
        }

        setPlaceMeta({
          source: place?.source || "google",
          layer: place?.layer || place?.type || "venue",
          sourceId: place?.sourceId || "",
          addressName: place?.addressName || "",
          addressNumber: place?.addressNumber || "",
          addressStreet: place?.addressStreet || "",
          addressLocality: place?.addressLocality || "",
          addressCountry: place?.addressCountry || "",
          addressRegion: place?.addressRegion || "",
          addressCounty: place?.addressCounty || "",
          addressNeighbourhood: place?.addressNeighbourhood || "",
          addressPlusCode: place?.addressPlusCode || "",
          propertiesSubcategory: place?.propertiesSubcategory || place?.type || "venue",
          propertiesImage: place?.propertiesImage || "",
          listImages: Array.isArray(place?.listImages) ? place.listImages : [],
          boundingBoxMinLat: place?.boundingBoxMinLat,
          boundingBoxMaxLat: place?.boundingBoxMaxLat,
          boundingBoxMinLon: place?.boundingBoxMinLon,
          boundingBoxMaxLon: place?.boundingBoxMaxLon,
          clientLoginId: place?.clientLoginId || "",
          partnerLoginId: place?.partnerLoginId || "",
          osmId: place?.osmId || "",
          osmType: place?.osmType || "",
          admin_level: place?.admin_level || 0,
        });
        setInitialSnapshot({
          name: (place?.name || "").trim(),
          type: (place?.type || "venue").trim(),
          lat: place?.centroidLat !== null && place?.centroidLat !== undefined ? Number(place.centroidLat).toFixed(6) : "",
          lng: place?.centroidLon !== null && place?.centroidLon !== undefined ? Number(place.centroidLon).toFixed(6) : "",
          sourceId: place?.sourceId || "",
          addressName: place?.addressName || "",
        });
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Erreur chargement du place",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [dispatch, isEditMode, placeId]);

  const coordinatesLabel = useMemo(() => {
    if (!marker) return "No marker selected";
    return `${marker.lat.toFixed(6)}, ${marker.lng.toFixed(6)}`;
  }, [marker]);

  const currentSnapshot = useMemo(
    () => ({
      name: (formik.values?.name || "").trim(),
      type: (formik.values?.type || "").trim(),
      lat: marker ? Number(marker.lat).toFixed(6) : "",
      lng: marker ? Number(marker.lng).toFixed(6) : "",
      sourceId: placeMeta?.sourceId || "",
      addressName: placeMeta?.addressName || "",
    }),
    [formik.values?.name, formik.values?.type, marker, placeMeta?.sourceId, placeMeta?.addressName]
  );

  const isEditDirty = useMemo(() => {
    if (!isEditMode) return true;
    if (!initialSnapshot) return false;
    return JSON.stringify(initialSnapshot) !== JSON.stringify(currentSnapshot);
  }, [isEditMode, initialSnapshot, currentSnapshot]);

  return (
    <Box sx={{ border: "1px solid #e5e7eb", borderRadius: "10px", background: "#fff" }}>
      <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{isEditMode ? "Edit Location" : "Add Location"}</Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>Add a new location to your map</Typography>
        </Box>
        <IconButton onClick={onCancel} sx={{ border: "1px solid #e2e8f0", borderRadius: "10px" }}>
          <Close />
        </IconButton>
      </Box>

      <Grid container spacing={2} sx={{ p: 2.5 }}>
        <Grid item xs={12} md={4}>
          <Box sx={{ border: "1px solid #e5e7eb", borderRadius: "12px", p: 2.2, minHeight: 450, background: "#f8fafc" }}>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2.2 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "#d1fae5", display: "grid", placeItems: "center" }}>
                <PlaceIcon sx={{ color: "#059669" }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700 }}>Location details</Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>Enter the basic information</Typography>
              </Box>
            </Box>
            <Grid container spacing={1.6}>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 0.7, fontWeight: 600 }}>Location Name <span style={{ color: "#ef4444" }}>*</span></Typography>
                <TextField name="name" placeholder="e.g. Dschang Market" value={formik.values.name} onChange={formik.handleChange} fullWidth size="small" error={Boolean(formik.touched.name && formik.errors.name)} helperText={formik.touched.name && formik.errors.name} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 0.7, fontWeight: 600 }}>Type <span style={{ color: "#ef4444" }}>*</span></Typography>
                <Autocomplete
                  options={TYPE_OPTIONS}
                  groupBy={(option) => `${option.domain} / ${option.group}`}
                  getOptionLabel={(option) => {
                    const fr = option.labelFr ? `${option.labelFr}` : "";
                    const en = option.labelEn ? ` (${option.labelEn})` : "";
                    return fr || option.label ? `${option.label}${en}` : option.tag || "";
                  }}
                  value={TYPE_OPTIONS.find((opt) => opt.tag === formik.values.type) || null}
                  onChange={(_, newValue) => formik.setFieldValue("type", newValue?.tag || "")}
                  isOptionEqualToValue={(option, value) => option.tag === value.tag}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search type..."
                      size="small"
                      error={Boolean(formik.touched.type && formik.errors.type)}
                      helperText={formik.touched.type && formik.errors.type}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, color: "#64748b" }}>
                  <InfoOutlined sx={{ fontSize: 16 }} />
                  <Typography variant="caption">Pick the location on map, and drag marker to fine tune.</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Photos</Typography>
                <Typography variant="caption" sx={{ color: "#64748b" }}>
                  Ajoutez des photos pour illustrer ce lieu (optionnel)
                </Typography>
                <Box sx={{ display: "flex", gap: 1.2, mt: 1.2 }}>
                  <Button
                    variant="contained"
                    onClick={() => cameraInputRef.current?.click()}
                    sx={{
                      flex: 1,
                      minHeight: 72,
                      borderRadius: "12px",
                      textTransform: "none",
                      background: "#0b1637",
                      color: "#fff",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    <AddAPhoto fontSize="small" />
                    Camera
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => galleryInputRef.current?.click()}
                    sx={{
                      flex: 1,
                      minHeight: 72,
                      borderRadius: "12px",
                      textTransform: "none",
                      background: "#0b1637",
                      color: "#fff",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    <AddPhotoAlternate fontSize="small" />
                    Galerie
                  </Button>
                </Box>
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} />
                <input ref={galleryInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          <MapPicker marker={marker} setMarker={setMarker} onPlaceMeta={setPlaceMeta} />
          <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#64748b" }}>
            Marker: {coordinatesLabel}
          </Typography>
        </Grid>
      </Grid>

      <Box sx={{ px: 2.5, py: 2, borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteOutline />}
          onClick={() => {
            setMarker(null);
            setPlaceMeta({});
          }}
          sx={{ borderRadius: "10px", textTransform: "none" }}
        >
          Delete marker
        </Button>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button variant="outlined" onClick={onCancel} sx={{ borderRadius: "10px", textTransform: "none", color: "#334155", borderColor: "#e2e8f0" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={formik.handleSubmit}
            disabled={isEditMode && !isEditDirty}
            sx={{ borderRadius: "10px", textTransform: "none", background: "#0f9d58" }}
          >
            {isEditMode ? "Update Location" : "Add Location"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AddPlaceForm;
